import { Toaster } from "@/components/ui/sonner";
import type { AuthContextType } from "@/contexts/AuthenticationProvider";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Link,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureRoutingError } from "@/lib/sentry";
import { useEffect } from "react";
import { Trans } from "react-i18next";

type RouterContext = {
  authentication?: AuthContextType | null;
  queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootRouteComponent,
  errorComponent: RootNotFoundComponent,
  notFoundComponent: RootNotFoundComponent,
});

function RootRouteComponent() {
  return (
    <>
      <HeadContent />
      <Outlet />
      <Toaster position={"bottom-center"} />
      <TanStackRouterDevtools />
    </>
  );
}

export function RootErrorComponent({ error }: { error: Error }) {
  useEffect(() => {
    if (error) captureRoutingError(error);
  }, [error]);

  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="max-w-md text-center">
        <AlertCircle className="mx-auto h-12 w-12 mb-4" />
        <h1 className="text-2xl font-bold mb-2">
          <Trans i18nKey={"rootErrorComponent.title"}>
            Something went wrong
          </Trans>
        </h1>
        <p className="text-muted-foreground mb-6">
          <Trans i18nKey={"rootErrorComponent.description"}>
            An unexpected error occurred.
          </Trans>
        </p>
        <Link to="/">
          <Button>
            <Trans i18nKey={"rootErrorComponent.buttonText"}>Go to home</Trans>
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function RootNotFoundComponent() {
  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="max-w-md text-center">
        <AlertCircle className="mx-auto h-12 w-12 mb-4" />
        <h1 className="text-2xl font-bold mb-2">
          <Trans i18nKey={"rootNonFoundComponent.title"}>Page not found</Trans>
        </h1>
        <p className="text-muted-foreground mb-6">
          <Trans i18nKey={"rootNonFoundComponent.description"}>
            The page you're looking for doesn't exist or has been moved.
          </Trans>
        </p>
        <Link to="/">
          <Button>
            <Trans i18nKey={"rootNonFoundComponent.buttonText"}>
              Go to home
            </Trans>
          </Button>
        </Link>
      </div>
    </div>
  );
}
